import React, { useState, useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api, adminApi, fileUrl } from "@/lib/api";
import { Upload, Trash2, Loader2, Image as ImageIcon, Check, X } from "lucide-react";

export default function AdminGallery({ adminKey }) {
  const { t } = useLanguage();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");
  const [initialized, setInitialized] = useState(false);
  const fileRef = useRef(null);

  const client = adminApi(adminKey);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/gallery");
      setImages(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    setInitialized(true);
    load();
  }

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await client.post("/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await client.post("/gallery", {
          image_url: up.data.url,
          label: "",
          sort_order: images.length + 1,
        });
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload misslyckades");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (img) => {
    if (!window.confirm(t.admin.gallery.confirm_delete)) return;
    await client.delete(`/gallery/${img.id}`);
    await load();
  };

  const startEditLabel = (img) => {
    setEditingLabelId(img.id);
    setLabelDraft(img.label || "");
  };
  const cancelLabel = () => {
    setEditingLabelId(null);
    setLabelDraft("");
  };
  const saveLabel = async (img) => {
    await client.patch(`/gallery/${img.id}`, { label: labelDraft });
    setEditingLabelId(null);
    setLabelDraft("");
    await load();
  };

  return (
    <div data-testid="admin-gallery-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <h2 className="font-display text-3xl md:text-4xl uppercase">
          {t.admin.gallery.title}
        </h2>
        <input
          type="file"
          ref={fileRef}
          multiple
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={onUpload}
          data-testid="admin-gallery-file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="admin-gallery-upload-btn"
          className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#ff7a30] transition-colors disabled:opacity-60"
        >
          {uploading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Upload size={14} />
          )}
          {uploading ? t.admin.gallery.uploading : t.admin.gallery.upload}
        </button>
      </div>

      {error && (
        <div className="mb-4 border border-red-600 bg-red-600/10 p-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">{t.common.loading}</div>
      ) : images.length === 0 ? (
        <div className="border border-zinc-800 bg-[#0d0d10] p-12 text-center text-zinc-500">
          <ImageIcon size={36} className="mx-auto mb-3 text-zinc-700" />
          {t.admin.gallery.empty}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((img) => (
            <article
              key={img.id}
              data-testid={`admin-gallery-card-${img.id}`}
              className="border border-zinc-800 bg-[#0d0d10] overflow-hidden group"
            >
              <div className="aspect-[4/3] bg-zinc-900 overflow-hidden">
                <img
                  src={fileUrl(img.image_url)}
                  alt={img.label || "gallery"}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-3 border-t border-zinc-800 space-y-2">
                {editingLabelId === img.id ? (
                  <div className="flex gap-1">
                    <input
                      autoFocus
                      value={labelDraft}
                      onChange={(e) => setLabelDraft(e.target.value)}
                      className="brut-input !py-1 !text-xs"
                      placeholder={t.admin.gallery.label}
                    />
                    <button
                      onClick={() => saveLabel(img)}
                      className="text-[#ff5a00] hover:text-[#ff7a30]"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={cancelLabel}
                      className="text-zinc-500 hover:text-white"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => startEditLabel(img)}
                    data-testid={`admin-gallery-label-${img.id}`}
                    className="text-xs font-mono tracking-widest uppercase text-zinc-400 hover:text-white text-left w-full truncate"
                  >
                    {img.label || `+ ${t.admin.gallery.label}`}
                  </button>
                )}
                <button
                  onClick={() => remove(img)}
                  data-testid={`admin-gallery-delete-${img.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 border border-zinc-800 text-zinc-500 px-2 py-1.5 text-[10px] tracking-widest uppercase hover:border-red-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={12} /> {t.admin.delete}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
